import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlockedIcon, MailIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { CommunicationsMessage } from '../communications.model';
import { COMMUNICATIONS_CTA_TYPES } from '../constants';
import { hasValidCommunicationDetails, openCommunicationContact } from '../utils';

interface CommunicationFullPageProps {
  message: CommunicationsMessage;
  onCtaClick: () => void;
}

const CommunicationFullPage: FC<CommunicationFullPageProps> = ({ message, onCtaClick }) => {
  const navigate = useNavigate();
  const [heading, ...bodyLines] = message.lines ?? [];
  const isEmailContact =
    Boolean(message.contact?.value) &&
    (message.contact?.type === 'EMAIL' || Boolean(message.contact?.value.includes('@')));
  const canOpenDetails =
    message.ctaType === COMMUNICATIONS_CTA_TYPES.DETAILS && hasValidCommunicationDetails(message.details);

  const handleContactClick = () => {
    openCommunicationContact(message.contact);
  };

  const handleCtaClick = () => {
    if (message.ctaType === COMMUNICATIONS_CTA_TYPES.DETAILS) {
      if (!canOpenDetails) return;
      navigate(routesPath.HOME_COMMUNICATION_DETAILS, { state: ROOT_ROUTE_STATE });
      return;
    }
    onCtaClick();
  };

  return (
    <div
      className="flex h-full flex-col justify-center gap-10 px-6 pt-32 md:gap-36"
      role="main"
      aria-label="Account status">
      <div className="mx-auto flex w-full grow flex-col items-center px-4 text-center sm:px-2.5">
        <BlockedIcon className="mb-5 shrink-0 [&_path]:fill-error" />
        {heading ? <div className="mb-4 text-xl font-bold text-headingGray">{heading}</div> : null}
        {bodyLines.length > 0 ? (
          <div className="flex flex-col gap-[18px]">
            {bodyLines.map((line, index) => (
              <div key={`communications-fullpage-line-${message.id}-${index}`} className="text-sbase text-primaryText">
                {line}
              </div>
            ))}
          </div>
        ) : null}

        {message.ctaText ? (
          <div className="mt-8">
            <CustomButton
              id={`communications-fullpage-cta-${message.id}`}
              className="h-10 px-6"
              childrenContainerStyles="text-sbase"
              type={ButtonType.PRIMARY}
              onClick={handleCtaClick}>
              {message.ctaText}
            </CustomButton>
          </div>
        ) : null}

        {message.footer ? (
          <div className="mt-8 w-full max-w-md border-t border-borderGray pt-5">
            <div className="text-px13 font-normal leading-5 text-accentText">{message.footer}</div>
          </div>
        ) : null}

        {message.contact ? (
          <button
            type="button"
            id={`communications-fullpage-contact-${message.id}`}
            className="mt-4 flex cursor-pointer items-center gap-2 text-sbase text-secondary"
            onClick={handleContactClick}
            aria-label={message.contact.label || message.contact.value}
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleContactClick();
              }
            }}>
            {isEmailContact ? <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
            {message.contact.label || message.contact.value}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CommunicationFullPage;
